// src/Upload Doc Invoice/DocumentFieldsService.js (Multi-Tenant Version)
class DocumentFieldsService {
  constructor() {
    this.documentFields = [];
    this.listeners = [];
    this.isInitialized = false;
    this.isInitializing = false;
    this.subscriptionActive = false;
    this.lastFirebaseData = null;
    this.unsubscribe = null;
    this.currentTenant = null; // Track current company/tenant
    this.tenantData = new Map(); // Cache data per tenant
    
    // Don't auto-initialize - wait for tenant context
  }

  // Set the current tenant/company context
  setTenant(companyId, companyName = null) {
    if (this.currentTenant === companyId) {
      console.log('DocumentFieldsService: Tenant already set to', companyId);
      return;
    }

    console.log('DocumentFieldsService: Switching tenant from', this.currentTenant, 'to', companyId);
    
    // Cleanup previous tenant subscription
    this.cleanup();
    
    // Set new tenant
    this.currentTenant = companyId;
    this.isInitialized = false;
    this.isInitializing = false;
    
    // Initialize for new tenant
    if (companyId) {
      this.initialize();
    }
  }

  // Get current tenant context
  getTenant() {
    return this.currentTenant;
  }

  // Check if service has valid tenant context
  requiresTenant() {
    if (!this.currentTenant) {
      throw new Error('DocumentFieldsService: No tenant context set. Call setTenant() first.');
    }
  }

  // Get tenant-specific cache key
  getTenantKey() {
    this.requiresTenant();
    return `tenant_${this.currentTenant}`;
  }

  // Get default fields (same for all tenants)
  getDefaultFields() {
    return [
      { 
        id: 'license', 
        label: 'Driving License', 
        enabled: true, 
        required: true, 
        icon: 'CreditCard', 
        color: 'blue-500' 
      },
      { 
        id: 'selfie', 
        label: 'Photo with License', 
        enabled: true, 
        required: true, 
        icon: 'Camera', 
        color: 'green-500' 
      },
      { 
        id: 'nic', 
        label: 'National ID Card', 
        enabled: false, 
        required: false, 
        icon: 'Hash', 
        color: 'orange-500' 
      },
      { 
        id: 'passport', 
        label: 'Passport', 
        enabled: false, 
        required: false, 
        icon: 'FileText', 
        color: 'purple-500' 
      }
    ];
  }

  // Merge default fields with saved fields (in memory only - no auto-save)
  mergeWithDefaults(savedFields) {
    const defaults = this.getDefaultFields();
    const savedFieldsMap = new Map(savedFields.map(field => [field.id, field]));
    
    // Start with default fields, but update them with saved values if they exist
    const mergedFields = defaults.map(defaultField => {
      const savedField = savedFieldsMap.get(defaultField.id);
      return savedField ? { ...defaultField, ...savedField } : defaultField;
    });
    
    // Add any custom fields (non-default fields) from saved data
    const defaultIds = new Set(defaults.map(field => field.id));
    const customFields = savedFields.filter(field => !defaultIds.has(field.id));
    
    return [...mergedFields, ...customFields];
  }

  // Initialize Firebase connection for current tenant
  async initialize() {
    this.requiresTenant();
    
    // Prevent duplicate initialization
    if (this.isInitialized || this.isInitializing) {
      console.log('DocumentFieldsService: Already initialized or initializing for tenant', this.currentTenant);
      return;
    }
    
    this.isInitializing = true;
    
    try {
      console.log('DocumentFieldsService: Initializing for tenant', this.currentTenant);
      
      // Load initial data from localStorage (multi-tenant)
      const initialFields = await this.getDocumentFieldsFromStorage();
      console.log('Initial fields loaded for tenant', this.currentTenant, ':', initialFields);
      
      const sanitizedFields = this.sanitizeFields(initialFields);
      
      // Store the initial data for comparison
      this.lastFirebaseData = JSON.stringify(sanitizedFields);
      
      // Cache data for this tenant
      const tenantKey = this.getTenantKey();
      this.tenantData.set(tenantKey, {
        fields: sanitizedFields,
        lastUpdate: Date.now()
      });
      
      // Merge with defaults in memory only (no auto-save)
      this.documentFields = this.mergeWithDefaults(sanitizedFields);
      this.isInitialized = true;
      this.isInitializing = false;
      this.notifyListeners();
      
      console.log('DocumentFieldsService: Initial data loaded and processed for tenant', this.currentTenant);
      
      // Set up localStorage subscription
      this.setupStorageSubscription();
      
      console.log('DocumentFieldsService: Initialized successfully for tenant', this.currentTenant, 'with fields:', this.documentFields);
    } catch (error) {
      console.error('DocumentFieldsService: Error initializing for tenant', this.currentTenant, ':', error);
      // On error, just use defaults (don't auto-save)
      this.documentFields = this.getDefaultFields();
      this.isInitialized = true;
      this.isInitializing = false;
      this.notifyListeners();
    }
  }

  // Get document fields from localStorage (multi-tenant)
  async getDocumentFieldsFromStorage() {
    this.requiresTenant();
    
    try {
      const allCompanyData = localStorage.getItem('documentFields_companies') || '{}';
      const companiesData = JSON.parse(allCompanyData);
      return companiesData[this.currentTenant] || [];
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return [];
    }
  }

  // Save document fields to localStorage (multi-tenant)
  async saveDocumentFieldsToStorage(fields) {
    this.requiresTenant();
    
    try {
      const allCompanyData = localStorage.getItem('documentFields_companies') || '{}';
      const companiesData = JSON.parse(allCompanyData);
      
      // Update this company's data
      companiesData[this.currentTenant] = fields.map(field => ({
        ...field,
        lastUpdated: new Date().toISOString(),
        companyId: this.currentTenant
      }));
      
      // Save back to localStorage
      localStorage.setItem('documentFields_companies', JSON.stringify(companiesData));
      
      console.log('✅ Document fields saved for tenant', this.currentTenant);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      throw error;
    }
  }

  // Set up localStorage subscription for current tenant
  setupStorageSubscription() {
    this.requiresTenant();
    
    if (this.subscriptionActive) {
      console.log('Storage subscription already active for tenant', this.currentTenant);
      return;
    }
    
    console.log('Setting up storage subscription for tenant', this.currentTenant);
    this.subscriptionActive = true;
    
    let lastKnownData = null;
    
    const checkForChanges = () => {
      try {
        const currentFields = this.getDocumentFieldsFromStorage();
        const currentDataString = JSON.stringify(currentFields);
        
        if (currentDataString !== lastKnownData) {
          lastKnownData = currentDataString;
          console.log('🔄 Document fields changed for tenant', this.currentTenant, ', notifying subscribers');
          
          const sanitizedFields = this.sanitizeFields(currentFields);
          this.documentFields = this.mergeWithDefaults(sanitizedFields);
          this.notifyListeners();
        }
      } catch (error) {
        console.error('Error in subscription check for tenant', this.currentTenant, ':', error);
      }
    };

    // Initial call
    checkForChanges();
    
    // Set up polling for changes
    this.intervalId = setInterval(checkForChanges, 1000);
    
    // Listen for localStorage changes from other tabs/windows
    this.storageListener = (event) => {
      if (event.key === 'documentFields_companies') {
        checkForChanges();
      }
    };
    
    window.addEventListener('storage', this.storageListener);
  }

  // Sanitize fields to ensure all properties are valid
  sanitizeFields(fields) {
    if (!Array.isArray(fields)) {
      return [];
    }

    return fields
      .filter(field => field && typeof field === 'object')
      .map(field => ({
        id: field.id || `field_${Date.now()}_${Math.random()}`,
        label: field.label || 'Unnamed Field',
        enabled: Boolean(field.enabled),
        required: Boolean(field.required),
        icon: field.icon || 'File',
        color: field.color || 'blue-500'
      }))
      .filter(field => field.label && field.label.trim())
      .map(field => ({
        ...field,
        label: field.label.trim()
      }));
  }

  // Get current fields for current tenant
  getDocumentFields() {
    this.requiresTenant();
    
    try {
      const fields = Array.isArray(this.documentFields) ? this.documentFields : this.getDefaultFields();
      return this.sanitizeFields(fields);
    } catch (error) {
      console.error('Error getting document fields for tenant', this.currentTenant, ':', error);
      return this.getDefaultFields();
    }
  }

  // Get enabled fields only for current tenant
  getEnabledDocumentFields() {
    this.requiresTenant();
    
    try {
      return this.getDocumentFields().filter(field => Boolean(field.enabled));
    } catch (error) {
      console.error('Error getting enabled fields for tenant', this.currentTenant, ':', error);
      return [];
    }
  }

  // Save fields directly for current tenant
  async saveFieldsDirectly(fields) {
    this.requiresTenant();
    
    const sanitizedFields = this.sanitizeFields(fields);
    await this.saveDocumentFieldsToStorage(sanitizedFields);
    
    // Update our tracking of what's stored
    this.lastFirebaseData = JSON.stringify(sanitizedFields);
    
    // Update tenant cache
    const tenantKey = this.getTenantKey();
    this.tenantData.set(tenantKey, {
      fields: sanitizedFields,
      lastUpdate: Date.now()
    });
    
    return sanitizedFields;
  }

  // Update a specific field for current tenant
  async updateField(fieldId, updates) {
    this.requiresTenant();
    
    try {
      // Sanitize updates
      const sanitizedUpdates = {};
      if (updates.label !== undefined) {
        if (!updates.label || typeof updates.label !== 'string' || !updates.label.trim()) {
          throw new Error('Field label must be a valid non-empty string');
        }
        sanitizedUpdates.label = updates.label.trim();
      }
      if (updates.enabled !== undefined) sanitizedUpdates.enabled = Boolean(updates.enabled);
      if (updates.required !== undefined) sanitizedUpdates.required = Boolean(updates.required);
      if (updates.icon !== undefined) sanitizedUpdates.icon = updates.icon || 'File';
      if (updates.color !== undefined) sanitizedUpdates.color = updates.color || 'blue-500';

      // Get current stored fields for this tenant
      const currentStoredFields = await this.getDocumentFieldsFromStorage();
      const sanitizedStoredFields = this.sanitizeFields(currentStoredFields);
      
      // Find if this field already exists in stored fields
      const existingFieldIndex = sanitizedStoredFields.findIndex(f => f.id === fieldId);
      
      if (existingFieldIndex >= 0) {
        // Update existing stored field
        sanitizedStoredFields[existingFieldIndex] = { 
          ...sanitizedStoredFields[existingFieldIndex], 
          ...sanitizedUpdates 
        };
      } else {
        // This is a default field being modified for the first time
        const defaultField = this.getDefaultFields().find(d => d.id === fieldId);
        if (defaultField) {
          // Add the modified default field to stored fields
          const modifiedField = { ...defaultField, ...sanitizedUpdates };
          sanitizedStoredFields.push(modifiedField);
        }
      }
      
      // Save only the stored fields
      await this.saveFieldsDirectly(sanitizedStoredFields);
      
      // Update internal state with merged defaults
      this.documentFields = this.mergeWithDefaults(sanitizedStoredFields);
      this.notifyListeners();
    } catch (error) {
      throw error;
    }
  }

  // Add new field for current tenant
  async addCustomField(fieldData) {
    this.requiresTenant();
    
    try {
      // Validate required data
      if (!fieldData || !fieldData.label || typeof fieldData.label !== 'string' || !fieldData.label.trim()) {
        throw new Error('Field label is required and must be a valid string');
      }

      const cleanLabel = fieldData.label.trim();
      const newId = cleanLabel.toLowerCase().replace(/[^a-z0-9]/g, '_') + '_' + Date.now();
      
      const newField = {
        id: newId,
        label: cleanLabel,
        enabled: Boolean(fieldData.enabled),
        required: Boolean(fieldData.required),
        icon: fieldData.icon || 'File',
        color: fieldData.color || 'blue-500'
      };

      // Get current stored fields and add the new one for this tenant
      const currentStoredFields = await this.getDocumentFieldsFromStorage();
      const sanitizedStoredFields = this.sanitizeFields(currentStoredFields);
      const updatedStoredFields = [...sanitizedStoredFields, newField];
      
      await this.saveFieldsDirectly(updatedStoredFields);
      
      // Update internal state with merged defaults
      this.documentFields = this.mergeWithDefaults(updatedStoredFields);
      this.notifyListeners();
      return newField;
    } catch (error) {
      throw error;
    }
  }

  // Toggle field enabled status for current tenant
  async toggleField(fieldId) {
    this.requiresTenant();
    
    const field = this.documentFields.find(f => f.id === fieldId);
    if (field) {
      await this.updateField(fieldId, { enabled: !field.enabled });
    }
  }

  // Remove field for current tenant
  async removeField(fieldId) {
    this.requiresTenant();
    
    try {
      const defaultFieldIds = ['license', 'selfie', 'nic', 'passport'];
      if (defaultFieldIds.includes(fieldId)) {
        throw new Error('Cannot remove default fields. You can disable them instead.');
      }
      
      // Get current stored fields and remove the specified one for this tenant
      const currentStoredFields = await this.getDocumentFieldsFromStorage();
      const sanitizedStoredFields = this.sanitizeFields(currentStoredFields);
      const updatedStoredFields = sanitizedStoredFields.filter(field => field.id !== fieldId);
      
      await this.saveFieldsDirectly(updatedStoredFields);
      
      // Update internal state with merged defaults
      this.documentFields = this.mergeWithDefaults(updatedStoredFields);
      this.notifyListeners();
    } catch (error) {
      throw error;
    }
  }

  // Enable all fields for current tenant
  async enableAllFields() {
    this.requiresTenant();
    
    try {
      // Get all current fields and enable them
      const allFields = this.getDocumentFields().map(field => ({ ...field, enabled: true }));
      
      // Only save the ones that are different from defaults or are custom fields
      const fieldsToSave = allFields.filter(field => {
        const defaultField = this.getDefaultFields().find(d => d.id === field.id);
        if (!defaultField) {
          return true; // Custom field - always save
        }
        return JSON.stringify(field) !== JSON.stringify(defaultField);
      });
      
      await this.saveFieldsDirectly(fieldsToSave);
      
      // Update internal state
      this.documentFields = allFields;
      this.notifyListeners();
    } catch (error) {
      throw error;
    }
  }

  // Disable all fields for current tenant
  async disableAllFields() {
    this.requiresTenant();
    
    try {
      // Get all current fields and disable them
      const allFields = this.getDocumentFields().map(field => ({ ...field, enabled: false }));
      
      // Only save the ones that are different from defaults or are custom fields
      const fieldsToSave = allFields.filter(field => {
        const defaultField = this.getDefaultFields().find(d => d.id === field.id);
        if (!defaultField) {
          return true; // Custom field - always save
        }
        return JSON.stringify(field) !== JSON.stringify(defaultField);
      });
      
      await this.saveFieldsDirectly(fieldsToSave);
      
      // Update internal state
      this.documentFields = allFields;
      this.notifyListeners();
    } catch (error) {
      throw error;
    }
  }

  // Subscribe to changes for current tenant
  subscribe(callback) {
    this.listeners.push(callback);
    
    // Call immediately if initialized
    if (this.isInitialized && this.currentTenant) {
      callback(this.getDocumentFields());
    }
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  // Notify listeners
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.getDocumentFields());
      } catch (error) {
        console.error('Error in listener for tenant', this.currentTenant, ':', error);
      }
    });
  }

  // Get statistics for current tenant
  getFieldStatistics() {
    if (!this.currentTenant) {
      return { total: 0, enabled: 0, required: 0, disabled: 0 };
    }
    
    try {
      const validFields = Array.isArray(this.documentFields) 
        ? this.documentFields.filter(field => field && field.id && field.label)
        : [];
        
      const total = validFields.length;
      const enabled = validFields.filter(f => Boolean(f.enabled)).length;
      const required = validFields.filter(f => Boolean(f.required)).length;
      
      return {
        total,
        enabled,
        required,
        disabled: total - enabled
      };
    } catch (error) {
      console.error('Error calculating statistics for tenant', this.currentTenant, ':', error);
      return { total: 0, enabled: 0, required: 0, disabled: 0 };
    }
  }

  // Export configuration for current tenant
  exportConfiguration() {
    this.requiresTenant();
    
    return {
      tenantId: this.currentTenant,
      fields: this.getDocumentFields(),
      timestamp: new Date().toISOString()
    };
  }

  // Import configuration for current tenant
  async importConfiguration(config) {
    this.requiresTenant();
    
    if (config.fields && Array.isArray(config.fields)) {
      const sanitizedFields = this.sanitizeFields(config.fields);
      await this.saveFieldsDirectly(sanitizedFields);
      
      // Update internal state
      this.documentFields = this.mergeWithDefaults(sanitizedFields);
      this.notifyListeners();
    }
  }

  // Get tenant cache info (for debugging)
  getTenantCacheInfo() {
    return {
      currentTenant: this.currentTenant,
      cacheSize: this.tenantData.size,
      tenants: Array.from(this.tenantData.keys())
    };
  }

  // Cleanup method
  cleanup() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
    this.subscriptionActive = false;
    this.lastFirebaseData = null;
    this.isInitialized = false;
    this.isInitializing = false;
  }

  // Destroy service completely
  destroy() {
    this.cleanup();
    this.listeners = [];
    this.currentTenant = null;
    this.tenantData.clear();
  }
}

// Create singleton
const documentFieldsService = new DocumentFieldsService();
export default documentFieldsService;